import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RoutingService } from '@rci/routing-service';
import { StateService } from '@rci/state-service';
import { AnalyticsError, IAccount, IUniversalDataLayer } from '@rogers/analytics';
import { combineLatest, merge, Observable, of, ReplaySubject } from 'rxjs';
import { delay, filter, map, skip, switchMap, take } from 'rxjs/operators';
import { UserInformationStateFacade } from '@rci/self-serve-fido/state/user-information/user-information-state.facade';
import { AccountDetailService } from '@rci/self-serve-fido/state/account/account-detail.service';

const myAccount = 'myaccount';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsStateService {
  page$: ReplaySubject<string | IUniversalDataLayer> = new ReplaySubject<string | IUniversalDataLayer>(1);
  personalization$: Observable<any> = of({ segment: null });
  globalAPI: string[] = ['/pages/api/selfserve/v4/login'];
  /*
    needs to be ReplaySubject otherwise _account$ emits before
    the switchMap in page$ has a change to subscribe
  */
  private readonly _account$: ReplaySubject<IAccount> = new ReplaySubject<IAccount>();
  account$: Observable<any> = this._account$.asObservable();

  constructor(
    private readonly accountDetailService: AccountDetailService,
    private readonly routingService: RoutingService,
    private readonly stateService: StateService,
    private readonly userInformationStateFacade: UserInformationStateFacade,
  ) {
    this.setAccount();
    const page$ = merge(this.routingService.url$, this.stateService.url$.pipe(filter(url => !!url)));
    merge(
      page$.pipe(
        take(1),
        map(path => (typeof path !== 'object' ? `/${myAccount}${path}` : path)),
        switchMap(page => this.account$.pipe(map(_acc => page))),
        take(1),
      ),
      // need to delay the account$ so that this emits after the above switchMap
      combineLatest([page$.pipe(skip(1)), this.account$.pipe(take(1), delay(0))]).pipe(
        map(([path, _acc$]) => (typeof path !== 'object' ? `/${myAccount}${path}` : path)),
      ),
    ).subscribe(page => {
      this.page$.next(page);
    });
  }

  shouldHandleError(err: HttpErrorResponse): boolean {
    return this.globalAPI.indexOf(err.url) >= 0;
  }

  getAPIError(err: HttpErrorResponse) {
    return [
      {
        category: 'self serve',
        type: 'http',
        code: err.status.toString(),
        API: err.url,
        field: '',
        message: err.message,
      } as AnalyticsError,
    ];
  }

  private getSelectedAccountNumber(): Observable<string> {
    return this.userInformationStateFacade.getSelectedAccountNumber();
  }

  private getUserType(): Observable<string> {
    return this.userInformationStateFacade.getUserType();
  }

  private getUserName(): Observable<string> {
    return this.userInformationStateFacade.getUserName();
  }

  private getSelectedPhoneNumber(): Observable<string> {
    return this.userInformationStateFacade.getSelectedPhoneNumber();
  }

  private getAccountType(): Observable<string> {
    return this.accountDetailService.selectedAccountType$.pipe(map(accountType => accountType.toLowerCase()));
  }

  private setAccount() {
    combineLatest([
      this.getSelectedAccountNumber(),
      this.getSelectedPhoneNumber(),
      this.getUserType(),
      this.getUserName(),
      this.getAccountType(),
    ]).subscribe(([ban, ctn, userType, userName, accType]: [string, string, string, string, string]) => {
      this._account$.next({
        BAN: ban ? parseInt(ban, 10) : null,
        CTN: ctn ? parseInt(ctn, 10) : null,
        type: accType,
        userName,
        userType,
      });
    });
  }
}
